import { Injectable, signal, WritableSignal } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { catchError, lastValueFrom, Observable, of } from "rxjs";
import { environment } from "../../environments/environment";
import { FeatureFlag } from "../models/feature-flag.model";
import { FeatureFlagEnum } from "../models/feature-flag.enum";

/**
 * PURPOSE: responsible for feature flag management throughout the application
 */
@Injectable({
  providedIn: "root"
})
export class FeatureFlagService {
  // Store for all feature
  private featureFlagsStore: WritableSignal<Record<string, boolean>> = signal<
    Record<string, boolean>
  >({});

  // Loading state
  private loadingState: WritableSignal<boolean> = signal<boolean>(false);

  // Initialization state
  private initialized: boolean = false;

  constructor(private http: HttpClient) {}

  /**
   * Initialize the feature flags service by loading all feature flags
   */
  public async initialize(): Promise<boolean> {
    if (this.initialized) {
      return true;
    }

    this.loadingState.set(true);

    try {
      // Create array of promises for each feature flag
      const flagPromises: Promise<FeatureFlag>[] = Object.values(FeatureFlagEnum).map(
        async (flagName: FeatureFlagEnum): Promise<FeatureFlag> => {
          try {
            return await lastValueFrom(this.getFeatureFlag(flagName));
          } catch (error) {
            console.warn(`Failed to fetch feature flag ${flagName}:`, error);
            // Return a default disabled flag if there's an error
            return { name: flagName, description: "", enabled: false };
          }
        }
      );

      // Wait for all promises to resolve
      const flags: FeatureFlag[] = await Promise.all(flagPromises);

      // Update the store with the fetched flags
      const store: Record<string, boolean> = {};
      flags.forEach((flag: FeatureFlag): void => {
        if (flag) {
          store[flag.name] = flag.enabled;
        }
      });

      this.featureFlagsStore.set(store);
      this.initialized = true;
      this.loadingState.set(false);
      return true;
    } catch (error) {
      console.error("Failed to initialize feature flags:", error);
      this.loadingState.set(false);
      return false;
    }
  }

  /**
   * Checks if a specific feature flag is enabled
   * @param flag The feature flag to check
   * @returns True if the flag is enabled, false otherwise
   */
  public isEnabled(flag: FeatureFlagEnum): boolean {
    return this.featureFlagsStore()[flag];
  }

  /**
   * Create a new feature flag
   * @param featureFlag The  feature flag to create
   * @returns An Observable that emits the newly created feature flag
   */
  public createFeatureFlag(featureFlag: FeatureFlag): Observable<FeatureFlag> {
    return this.http
      .post<FeatureFlag>(
        `${environment.fulcrumUrl}/api/v3/feature-flag`,
        featureFlag,
        this.buildBasicHttpOptions()
      )
      .pipe(
        catchError((error: HttpErrorResponse): Observable<FeatureFlag> => {
          console.error(`Error creating feature flag ${featureFlag.name}:`, error);
          throw error;
        })
      );
  }

  /**
   * Create a new feature flag
   * @param featureFlag The  feature flag to create
   * @returns An Observable that emits the newly created feature flag
   */
  public updateFeatureFlag(featureFlag: FeatureFlag): Observable<FeatureFlag> {
    return this.http
      .post<FeatureFlag>(
        `${environment.fulcrumUrl}/api/v3/feature-flag/${featureFlag.id}`,
        featureFlag,
        this.buildBasicHttpOptions()
      )
      .pipe(
        catchError((error: HttpErrorResponse): Observable<FeatureFlag> => {
          console.error(`Error updating feature flag ${featureFlag.name}:`, error);
          throw error;
        })
      );
  }

  /**
   * Retrieves a single feature flag
   * @param flagName The name of the feature flag to retrieve
   * @returns An Observable that emits the feature flag
   */
  private getFeatureFlag(flagName: string): Observable<FeatureFlag> {
    return this.http
      .get<FeatureFlag>(
        `${environment.fulcrumUrl}/api/v3/feature-flag/${flagName}`,
        this.buildBasicHttpOptions()
      )
      .pipe(
        catchError((error: HttpErrorResponse): Observable<FeatureFlag> => {
          console.error(`Error fetching feature flag ${flagName}:`, error);
          return of({ name: flagName, description: "", enabled: false });
        })
      );
  }

  /**
   * Gets all feature flags
   * @returns An Observable that emits an array of FeatureFlag objects
   */
  public getFeatureFlags(): Observable<FeatureFlag[]> {
    return this.http
      .get<
        FeatureFlag[]
      >(`${environment.fulcrumUrl}/api/v3/feature-flag`, this.buildBasicHttpOptions())
      .pipe(
        catchError((error: HttpErrorResponse): Observable<FeatureFlag[]> => {
          console.error("Error fetching feature flags:", error);
          return of([]);
        })
      );
  }

  private buildBasicHttpOptions() {
    return {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        Authorization:
          "Basic " + btoa(`${environment.claimItApiUsername}:${environment.claimItApiPassword}`)
      }),
      params: {}
    };
  }
}
